import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { Screen } from '../components/layout';
import { ListingGrid, ListingGridSkeleton } from '../components/listing';
import { CategoryPickerSheet, LocationPickerSheet, useCategoryPath, useLocationPath } from '../components/pickers';
import { Button, Chip, EmptyState, ErrorState, Field } from '../components/ui';
import { parseSmartQuery } from '@qm/shared';
import { api } from '../lib/api';
import { shortPlace } from '../lib/format';
import { useCategories, useCategoryAttributes, useLocations, usePopularSearches, useRecentSearches, useSearch, useSearchCount, useVipStrip, type SearchParams } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';

const SORTS = ['newest', 'price_asc', 'price_desc', 'relevance'] as const;

/** Состояние поиска хранится в URL — им можно поделиться, и «Назад» его восстанавливает. */
function readParams(sp: URLSearchParams): SearchParams {
  const attrs: Record<string, string> = {};
  for (const [k, v] of sp) if (k.startsWith('attr.')) attrs[k.slice(5)] = v;
  return {
    q: sp.get('q') ?? undefined,
    categoryId: sp.get('categoryId') ?? undefined,
    locationId: sp.get('locationId') ?? undefined,
    priceMin: sp.get('priceMin') ?? undefined,
    priceMax: sp.get('priceMax') ?? undefined,
    condition: (sp.get('condition') as SearchParams['condition']) ?? undefined,
    withPhotos: sp.get('withPhotos') === 'true' || undefined,
    negotiable: sp.get('negotiable') === 'true' || undefined,
    sellerType: (sp.get('sellerType') as SearchParams['sellerType']) ?? undefined,
    sort: (sp.get('sort') as SearchParams['sort']) ?? undefined,
    attrs,
  };
}

function writeParams(p: SearchParams): URLSearchParams {
  const sp = new URLSearchParams();
  const set = (k: string, v: string | undefined | boolean) => {
    if (v !== undefined && v !== '' && v !== false) sp.set(k, String(v));
  };
  set('q', p.q);
  set('categoryId', p.categoryId);
  set('locationId', p.locationId);
  set('priceMin', p.priceMin);
  set('priceMax', p.priceMax);
  set('condition', p.condition);
  set('withPhotos', p.withPhotos);
  set('negotiable', p.negotiable);
  set('sellerType', p.sellerType);
  set('sort', p.sort && p.sort !== 'newest' ? p.sort : undefined);
  for (const [k, v] of Object.entries(p.attrs ?? {})) set(`attr.${k}`, v);
  return sp;
}

export function SearchPage() {
  const { t } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const params = useMemo(() => readParams(sp), [sp]);
  const [text, setText] = useState(params.q ?? '');
  // Фильтры открыты — ?filters=1 в адресе: кнопка «Назад» Telegram закрывает их, а не уходит с экрана поиска.
  const filtersOpen = sp.get('filters') === '1';
  const navigate = useNavigate();
  const openedHere = useRef(false);
  const setFiltersOpen = (open: boolean) => {
    const next = new URLSearchParams(sp);
    if (open) {
      openedHere.current = true;
      next.set('filters', '1');
      setSp(next);
    } else if (openedHere.current) {
      openedHere.current = false;
      navigate(-1);
    } else {
      next.delete('filters');
      setSp(next, { replace: true });
    }
  };
  const [pickCategory, setPickCategory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const hasCriteria = Boolean(
    params.q || params.categoryId || params.locationId || params.priceMin || params.priceMax || params.condition || params.withPhotos || params.negotiable || params.sellerType || Object.keys(params.attrs ?? {}).length,
  );
  const search = useSearch(params);
  const recent = useRecentSearches();
  const popularQueries = usePopularSearches();
  const categoryPath = useCategoryPath(params.categoryId);
  const locationPath = useLocationPath(params.locationId);

  useEffect(() => {
    if (sp.get('focus') === '1') inputRef.current?.focus();
  }, [sp]);
  useEffect(() => setText(params.q ?? ''), [params.q]);

  const update = (patch: Partial<SearchParams>) => setSp(writeParams({ ...params, ...patch }), { replace: true });

  // Умный поиск: из фразы достаём цену, место, состояние, категорию; остальное — текст запроса.
  const cats = useCategories();
  const locs = useLocations();
  const [smart, setSmart] = useState<{ original: string; labels: string[] } | null>(null);
  const submitText = (raw: string) => {
    const value = raw.trim();
    const r = value ? parseSmartQuery(value, { categories: cats.data ?? [], locations: locs.data ?? [] }) : null;
    if (!r || !r.recognized.length) {
      setSmart(null);
      update({ q: value || undefined, sort: value && !params.sort ? 'relevance' : params.sort });
      return;
    }
    const money = (v?: string) => (v ? Number(v).toLocaleString('ru-RU') : '');
    const labels = [
      r.priceMin && r.priceMax
        ? `${money(r.priceMin)} – ${money(r.priceMax)} ${t('common.currency')}`
        : r.priceMax
          ? `${t('search.priceTo')} ${money(r.priceMax)} ${t('common.currency')}`
          : r.priceMin
            ? `${t('search.priceFrom')} ${money(r.priceMin)} ${t('common.currency')}`
            : '',
      r.categoryName ?? '',
      r.locationName ?? '',
      r.condition ? t(`condition.${r.condition}`) : '',
      r.withPhotos ? t('search.withPhotos') : '',
      r.negotiable ? t('listing.negotiable') : '',
    ].filter(Boolean);
    setSmart({ original: value, labels });
    setText(r.q);
    setSp(
      writeParams({
        ...params,
        q: r.q || undefined,
        priceMin: r.priceMin ?? params.priceMin,
        priceMax: r.priceMax ?? params.priceMax,
        categoryId: r.categoryId ?? params.categoryId,
        locationId: r.locationId ?? params.locationId,
        condition: r.condition ?? params.condition,
        withPhotos: r.withPhotos || params.withPhotos,
        negotiable: r.negotiable || params.negotiable,
        sort: r.q && !params.sort ? 'relevance' : params.sort,
      }),
      { replace: true },
    );
  };
  const undoSmart = () => {
    if (!smart) return;
    setText(smart.original);
    setSp(writeParams({ q: smart.original, sort: 'relevance' }), { replace: true });
    setSmart(null);
  };
  // VIP-строка — только при сортировке по умолчанию (при явной сортировке пользователь ждёт строгий порядок).
  const defaultSort = !params.sort || params.sort === (params.q ? 'relevance' : 'newest');
  const vip = useVipStrip(params, defaultSort);
  const vipItems = defaultSort ? (vip.data ?? []) : [];
  const vipIds = new Set(vipItems.map((x) => x.id));
  const items = (search.data?.pages.flatMap((p) => p.items) ?? []).filter((x) => !vipIds.has(x.id));
  const activeFilters =
    [params.locationId, params.priceMin || params.priceMax, params.condition, params.withPhotos, params.negotiable, params.sellerType].filter(Boolean).length +
    Object.keys(params.attrs ?? {}).length;

  // Подгрузка следующей страницы при прокрутке.
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && search.hasNextPage && !search.isFetchingNextPage) void search.fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [search]);

  return (
    <Screen back title={t('search.title')}>
      <form
        className="search-bar"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          inputRef.current?.blur();
          submitText(text);
        }}
      >
        <Icon name="search" size={20} />
        <input
          ref={inputRef}
          type="search"
          enterKeyHint="search"
          value={text}
          maxLength={100}
          placeholder={t('search.placeholder')}
          onChange={(e) => setText(e.target.value)}
          aria-label={t('search.placeholder')}
        />
        {text ? (
          <button type="button" className="icon-btn" aria-label={t('common.clear')} onClick={() => { setText(''); update({ q: undefined }); }}>
            <Icon name="close" size={18} />
          </button>
        ) : null}
      </form>

      <div className="chips-row">
        <Chip active={Boolean(params.categoryId)} onClick={() => setPickCategory(true)}>
          {categoryPath.length ? categoryPath[categoryPath.length - 1]!.name : t('search.allCategories')}
        </Chip>
        <Chip active={activeFilters > 0} onClick={() => setFiltersOpen(true)}>
          <Icon name="filter" size={16} /> {t('search.filters')}
          {activeFilters ? ` · ${activeFilters}` : ''}
        </Chip>
        {params.locationId && locationPath.length ? (
          <Chip active onClick={() => update({ locationId: undefined })}>
            <Icon name="pin" size={14} /> {shortPlace(locationPath[locationPath.length - 1]!.name)} ✕
          </Chip>
        ) : null}
      </div>

      {smart ? (
        <div className="smart-note" role="status">
          <Icon name="sparkle" size={18} />
          <span>
            {t('search.smartUnderstood')} <strong>{smart.labels.join(' · ')}</strong>
          </span>
          <button type="button" className="link" onClick={undoSmart}>
            {t('search.smartUndo')}
          </button>
        </div>
      ) : null}

      {!params.q && !hasCriteria ? (
        <>
          {popularQueries.data?.items.length ? (
            <section className="section suggest">
              <h2 className="suggest-title">{t('search.popular')}</h2>
              <div className="chips-row wrap">
                {popularQueries.data.items.map((r) => (
                  <Chip key={r} onClick={() => update({ q: r, sort: 'relevance' })}>
                    {r}
                  </Chip>
                ))}
              </div>
            </section>
          ) : null}
          <section className="smart-card">
            <span className="smart-card-icon">
              <Icon name="sparkle" size={22} />
            </span>
            <div className="smart-card-body">
              <h2>{t('search.smartTitle')}</h2>
              <p>{t('search.smartText')}</p>
              <Button
                size="sm"
                onClick={() => {
                  const ex = t('search.smartExample');
                  setText(ex);
                  submitText(ex);
                }}
              >
                {t('search.smartTry')}
              </Button>
            </div>
          </section>
          {recent.data && recent.data.length > 0 ? (
            <section className="section suggest">
              <div className="section-head">
                <h2 className="suggest-title">{t('search.history')}</h2>
                <button
                  type="button"
                  className="link"
                  onClick={async () => {
                    await api('/users/me/searches', { method: 'DELETE' });
                    void qc.invalidateQueries({ queryKey: ['recent-searches'] });
                  }}
                >
                  {t('common.clear')}
                </button>
              </div>
              <div className="history">
                {recent.data.map((r) => (
                  <button key={r} type="button" className="history-row" onClick={() => update({ q: r, sort: 'relevance' })}>
                    <Icon name="search" size={18} />
                    <span>{r}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      <div className="results-head">
        <span className="muted">{params.q ? t('search.resultsFor', { q: params.q }) : t('search.results')}</span>
        <select className="sort-select" value={params.sort ?? (params.q ? 'relevance' : 'newest')} onChange={(e) => update({ sort: e.target.value as SearchParams['sort'] })} aria-label={t('search.sort')}>
          {SORTS.filter((s) => s !== 'relevance' || params.q).map((s) => (
            <option key={s} value={s}>
              {t(`sort.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {search.isLoading ? (
        <ListingGridSkeleton />
      ) : search.error ? (
        <ErrorState error={search.error} onRetry={() => void search.refetch()} />
      ) : items.length === 0 && vipItems.length === 0 ? (
        <EmptyState
          title={t('search.nothingFound')}
          text={t('search.nothingFoundText')}
          action={
            hasCriteria ? (
              <Button variant="secondary" onClick={() => setSp(new URLSearchParams(), { replace: true })}>
                {t('search.reset')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {vipItems.length ? (
            <section className="vip-strip" aria-label={t('search.vip')}>
              <h2 className="tiny muted">{t('search.vip')}</h2>
              <ListingGrid items={vipItems} />
            </section>
          ) : null}
          <ListingGrid items={items} />
          <div ref={sentinel} className="sentinel">
            {search.isFetchingNextPage ? <span className="spinner" /> : null}
          </div>
        </>
      )}

      <CategoryPickerSheet
        open={pickCategory}
        onClose={() => setPickCategory(false)}
        selectedId={params.categoryId}
        onSelect={(c) => update({ categoryId: c?.id, attrs: {} })}
      />
      <FiltersSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        params={params}
        onApply={(p) => {
          openedHere.current = false;
          setSp(writeParams(p), { replace: true });
        }}
      />
    </Screen>
  );
}

/** Шкала ползунка цены (сум): мелкий шаг внизу, крупный вверху — удобно и для телефона, и для машины. */
const PRICE_STEPS = [0, 50_000, 100_000, 200_000, 300_000, 500_000, 750_000, 1_000_000, 1_500_000, 2_000_000, 3_000_000, 5_000_000, 7_500_000, 10_000_000, 15_000_000, 20_000_000, 30_000_000, 50_000_000, 75_000_000, 100_000_000, 150_000_000, 250_000_000, 500_000_000, 1_000_000_000];
const stepOf = (v: string | undefined, fallback: number) => {
  if (!v) return fallback;
  const n = Number(v);
  let i = 0;
  while (i < PRICE_STEPS.length - 1 && PRICE_STEPS[i + 1]! <= n) i++;
  return i;
};
const groupDigits = (v: string | undefined) => (v ? v.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : '');

function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Фильтры на весь экран (как на макете): продавец, цена с ползунком, категория, состояние, город, дополнительно. */
function FiltersSheet({ open, onClose, params, onApply }: { open: boolean; onClose: () => void; params: SearchParams; onApply: (p: SearchParams) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SearchParams>(params);
  const [pickLocation, setPickLocation] = useState(false);
  const [pickCategory, setPickCategory] = useState(false);
  const locationPath = useLocationPath(draft.locationId);
  const categoryPath = useCategoryPath(draft.categoryId);
  const attrs = useCategoryAttributes(draft.categoryId);
  useEffect(() => {
    if (open) setDraft(params);
  }, [open, params]);
  // Число объявлений по черновику — с небольшой задержкой, чтобы не спрашивать сервер на каждое движение ползунка.
  const [debounced, setDebounced] = useState(draft);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(draft), 350);
    return () => clearTimeout(h);
  }, [draft]);
  const count = useSearchCount(debounced, open);

  if (!open) return null;

  const setAttr = (key: string, value: string | undefined) => {
    const next = { ...(draft.attrs ?? {}) };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    setDraft({ ...draft, attrs: next });
  };
  const digits = (v: string) => v.replace(/[^\d]/g, '').slice(0, 13);
  const lo = stepOf(draft.priceMin, 0);
  const hi = draft.priceMax ? stepOf(draft.priceMax, PRICE_STEPS.length - 1) : PRICE_STEPS.length - 1;
  const last = PRICE_STEPS.length - 1;
  const n = count.data?.count;

  return (
    <div className="filters-screen" role="dialog" aria-modal="true" aria-label={t('search.filters')}>
      <header className="filters-head">
        <button type="button" className="icon-btn" aria-label={t('common.back')} onClick={onClose}>
          <Icon name="back" />
        </button>
        <h1>{t('search.filters')}</h1>
        <button type="button" className="link" onClick={() => setDraft({ q: draft.q, sort: draft.sort })}>
          {t('search.reset')}
        </button>
      </header>

      <div className="filters-body">
        <Segmented
          label={t('search.sellerType')}
          value={draft.sellerType ?? 'all'}
          options={[
            { value: 'all', label: t('search.sellerAll') },
            { value: 'private', label: t('search.sellerPrivate') },
            { value: 'business', label: t('search.sellerBusiness') },
          ]}
          onChange={(v) => setDraft({ ...draft, sellerType: v === 'all' ? undefined : v })}
        />

        <h2 className="filters-h">{t('search.price')}</h2>
        <div className="row-2">
          <label className="price-field">
            <span>{t('search.priceFrom')}</span>
            <input inputMode="numeric" aria-label={`${t('search.price')} ${t('search.priceFrom')}`} value={groupDigits(draft.priceMin)} placeholder="0" onChange={(e) => setDraft({ ...draft, priceMin: digits(e.target.value) || undefined })} />
          </label>
          <label className="price-field">
            <span>{t('search.priceTo')}</span>
            <input inputMode="numeric" aria-label={`${t('search.price')} ${t('search.priceTo')}`} value={groupDigits(draft.priceMax)} placeholder="∞" onChange={(e) => setDraft({ ...draft, priceMax: digits(e.target.value) || undefined })} />
          </label>
        </div>
        <div className="range" style={{ '--lo': `${(lo / last) * 100}%`, '--hi': `${(hi / last) * 100}%` } as React.CSSProperties}>
          <div className="range-track" />
          <input type="range" min={0} max={last} step={1} value={lo} aria-label={t('search.priceFrom')}
            onChange={(e) => { const i = Math.min(Number(e.target.value), hi); setDraft({ ...draft, priceMin: i > 0 ? String(PRICE_STEPS[i]) : undefined }); }} />
          <input type="range" min={0} max={last} step={1} value={hi} aria-label={t('search.priceTo')}
            onChange={(e) => { const i = Math.max(Number(e.target.value), lo); setDraft({ ...draft, priceMax: i < last ? String(PRICE_STEPS[i]) : undefined }); }} />
        </div>

        <h2 className="filters-h">{t('search.category')}</h2>
        <button type="button" className="select-like" onClick={() => setPickCategory(true)}>
          <span>{categoryPath.length ? categoryPath.map((c) => c.name).join(' › ') : t('search.allCategories')}</span>
          <Icon name="chevron" size={18} />
        </button>

        <h2 className="filters-h">{t('listing.condition')}</h2>
        <Segmented
          label={t('listing.condition')}
          value={draft.condition ?? 'any'}
          options={[
            { value: 'any', label: t('common.any') },
            { value: 'new', label: t('condition.new') },
            { value: 'used', label: t('condition.used') },
          ]}
          onChange={(v) => setDraft({ ...draft, condition: v === 'any' ? undefined : v })}
        />

        <h2 className="filters-h">{t('search.city')}</h2>
        <button type="button" className="select-like" onClick={() => setPickLocation(true)}>
          <span>{locationPath.length ? locationPath.map((l) => l.name).slice(-2).join(', ') : t('location.all')}</span>
          <Icon name="chevron" size={18} />
        </button>

        {(attrs.data ?? [])
          .filter((a) => a.filterable)
          .map((a) =>
            a.type === 'select' ? (
              <Field key={a.key} label={a.label}>
                <div className="chips-row wrap">
                  <Chip active={!draft.attrs?.[a.key]} onClick={() => setAttr(a.key, undefined)}>{t('common.any')}</Chip>
                  {a.options?.map((o) => (
                    <Chip key={o.value} active={draft.attrs?.[a.key] === o.value} onClick={() => setAttr(a.key, o.value)}>
                      {o.label}
                    </Chip>
                  ))}
                </div>
              </Field>
            ) : a.type === 'number' ? (
              <Field key={a.key} label={`${a.label}${a.unit ? `, ${a.unit}` : ''}`}>
                <div className="row-2">
                  <input inputMode="decimal" className="input" placeholder={t('search.priceFrom')} value={draft.attrs?.[`${a.key}.min`] ?? ''} onChange={(e) => setAttr(`${a.key}.min`, e.target.value.replace(/[^\d.]/g, ''))} />
                  <input inputMode="decimal" className="input" placeholder={t('search.priceTo')} value={draft.attrs?.[`${a.key}.max`] ?? ''} onChange={(e) => setAttr(`${a.key}.max`, e.target.value.replace(/[^\d.]/g, ''))} />
                </div>
              </Field>
            ) : null,
          )}

        <h2 className="filters-h">{t('search.more')}</h2>
        <label className="toggle-row">
          <span className="toggle-label">{t('search.withPhotos')}</span>
          <input type="checkbox" role="switch" checked={Boolean(draft.withPhotos)} onChange={(e) => setDraft({ ...draft, withPhotos: e.target.checked || undefined })} />
          <span className="toggle" aria-hidden />
        </label>
        <label className="toggle-row">
          <span className="toggle-label">{t('search.onlyNegotiable')}</span>
          <input type="checkbox" role="switch" checked={Boolean(draft.negotiable)} onChange={(e) => setDraft({ ...draft, negotiable: e.target.checked || undefined })} />
          <span className="toggle" aria-hidden />
        </label>
      </div>

      <div className="filters-foot">
        <Button
          block
          size="lg"
          onClick={() => {
            onApply(draft);
          }}
        >
          {n === undefined ? t('search.show') : n === 0 ? t('search.showNone') : t('search.showN', { count: n, n: count.data?.capped ? `${n.toLocaleString('ru-RU')}+` : n.toLocaleString('ru-RU') })}
        </Button>
      </div>

      <LocationPickerSheet open={pickLocation} onClose={() => setPickLocation(false)} selectedId={draft.locationId} onSelect={(l) => setDraft({ ...draft, locationId: l?.id })} />
      <CategoryPickerSheet open={pickCategory} onClose={() => setPickCategory(false)} selectedId={draft.categoryId} onSelect={(c) => setDraft({ ...draft, categoryId: c?.id, attrs: {} })} />
    </div>
  );
}
